/**
 * StaffAdrReportDialog — lets a pharmacist file a new adverse-drug-reaction
 * report in person (e.g. a walk-in customer mentions a reaction at the
 * counter). Separate from the patient portal's own submission flow.
 *
 * Works offline: if there's no connection (or the request drops mid-flight),
 * the report is queued in IndexedDB and sent automatically once the
 * terminal reconnects — same pattern as the offline sales queue.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Send } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { addOfflineAdrReport, generateOfflineId } from '../lib/offlineDB';

const emptyForm = { medicine_name: '', batch_number: '', reaction_description: '', severity: 'mild', patient_name: '', patient_phone: '', notes: '' };

export const StaffAdrReportDialog = ({ open, onOpenChange, onFiled }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target?.value ?? e }));

  const reset = () => setForm(emptyForm);

  const submit = async () => {
    if (!form.medicine_name.trim() || !form.reaction_description.trim()) {
      toast.error('Medicine and reaction description are required');
      return;
    }
    setSaving(true);
    try {
      if (!navigator.onLine) {
        await addOfflineAdrReport(form);
        toast.success('Saved offline — this report will be submitted once you reconnect');
      } else {
        // Same id sent either way, so a lost response can't file the report twice.
        const offline_id = generateOfflineId();
        try {
          await api.post('/staff/portal/adverse-reactions', { ...form, offline_id });
          toast.success('Reaction report filed');
        } catch (err) {
          if (err.response) throw err;
          await addOfflineAdrReport({ ...form, offline_id }); // network dropped mid-request
          toast.success('Saved offline — this report will be submitted once you reconnect');
        }
      }
      reset();
      onOpenChange(false);
      onFiled?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the report');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="staff-adr-file-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> File a reaction report</DialogTitle>
          <DialogDescription>For a reaction reported in person — works offline, syncs automatically once you reconnect.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="adr-patient-name">Patient name</Label>
              <Input id="adr-patient-name" value={form.patient_name} onChange={set('patient_name')} placeholder="Optional" data-testid="staff-adr-file-patient-name" />
            </div>
            <div>
              <Label htmlFor="adr-patient-phone">Patient phone</Label>
              <Input id="adr-patient-phone" value={form.patient_phone} onChange={set('patient_phone')} placeholder="Optional" data-testid="staff-adr-file-patient-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="adr-medicine">Medicine *</Label>
              <Input id="adr-medicine" value={form.medicine_name} onChange={set('medicine_name')} data-testid="staff-adr-file-medicine" />
            </div>
            <div>
              <Label htmlFor="adr-batch">Batch #</Label>
              <Input id="adr-batch" value={form.batch_number} onChange={set('batch_number')} placeholder="Optional" data-testid="staff-adr-file-batch" />
            </div>
          </div>
          <div>
            <Label htmlFor="adr-severity">Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm((p) => ({ ...p, severity: v }))}>
              <SelectTrigger id="adr-severity" data-testid="staff-adr-file-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mild">Mild</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="severe">Severe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="adr-description">What happened? *</Label>
            <Textarea id="adr-description" rows={3} value={form.reaction_description} onChange={set('reaction_description')} data-testid="staff-adr-file-description" />
          </div>
          <div>
            <Label htmlFor="adr-notes">Notes</Label>
            <Textarea id="adr-notes" rows={2} value={form.notes} onChange={set('notes')} placeholder="Optional" data-testid="staff-adr-file-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="staff-adr-file-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="gap-1.5" data-testid="staff-adr-file-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            File report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
